import React, { useState, useEffect, useCallback } from 'react'
import Footer from '../Component/NewFooter'
import SplashScreen from '../Component/SplashScreen'
import axios from 'axios'

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
const CACHE_KEY      = "jeyshawn_safety_data";

const resolveSrc = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value === "string") return value.startsWith("http") ? value : `${BASE_URL}/${value}`;
  return fallback;
};

/* ── Helpers ───────────────────────────────────────────────────── */
function parseSafetyData(db) {
  return {
    bannerImage:  db?.bannerImage  || "",
    title:        db?.title        || "",
    introHeading: db?.introHeading || "",
    paragraphs:   Array.isArray(db?.paragraphs) ? db.paragraphs : [],
    goalsHeading: db?.sections?.[0]?.heading                  || "",
    goalsItems:   Array.isArray(db?.sections?.[0]?.items)     ? db.sections[0].items : [],
  };
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────── */
const SafetyPolicy = () => {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/safety`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseSafetyData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/safety:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;

  const data = pageData;
  const hasContent = Boolean(data?.title);

  return (
    <div>
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      {hasContent && (
        <>
          <div className="container">
            <div className="content-wrapper">
              <div className="text-section">
                <p className="aboutusId wow animate__animated animate__fadeInUp">
                  <span className="aboutusId-link">About Us</span>
                  <span className="separator">{">"}</span>
                  <span className="aboutusId-link active">Safety Policy</span>
                </p>

                <h1 className="main-heading wow animate__animated animate__fadeInUp" data-wow-delay="0.3s">
                  {data.title}
                </h1>
              </div>
            </div>

            <div className="image-section wow animate__animated animate__fadeInUp" data-wow-delay="0.3s">
              <div className="slant-overlay"></div>
              {data.bannerImage && (
                <img
                  src={resolveSrc(data.bannerImage)}
                  alt="Construction workers operating heavy machinery"
                  className="background-image"
                />
              )}
            </div>
          </div>

          <div className="safetyPolicy_text">
            {data.introHeading && (
              <h2 className="wow animate__animated animate__fadeInUp" data-wow-delay="0.3s">
                {data.introHeading}
              </h2>
            )}

            {data.paragraphs.map((para, i) => (
              <p key={i} className="wow animate__animated animate__fadeInUp" data-wow-delay={`${0.3 + i * 0.1}s`}>
                {para}
              </p>
            ))}

            {data.goalsHeading && (
              <h2 className="safetyPolicy_title wow animate__animated animate__fadeInUp" data-wow-delay="0.3s">
                {data.goalsHeading}
              </h2>
            )}

            {data.goalsItems.map((para, i) => (
              <p key={i} className="wow animate__animated animate__fadeInUp" data-wow-delay={`${0.3 + i * 0.1}s`}>
                {para}
              </p>
            ))}
          </div>
        </>
      )}

      <Footer />
    </div>
  );
};

export default SafetyPolicy;
